/**
 * PosteingangZuordnungsForm – vollständiger Formularinhalt der MITTE-Spalte
 * Enthält: KI-Panel, Von/Für/Maßnahme/Aufgabe-Felder, Speichern-Buttons
 */
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Sparkles, Loader2, Check, X, Plus, Search, UserPlus,
  Save, SkipForward, User, Tag, Briefcase, Zap,
  Clock, Calendar, MessageSquare
} from 'lucide-react';
import PosteingangKiPanel from './PosteingangKiPanel';
import { toast } from 'sonner';


// ==================== ABSENDER-SUCHE KOMPONENTE ====================
function AbsenderSuche({ value, onChange, jurPersonen, kiEmpfehlung }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  
  const selected = jurPersonen.find(j => j.id === value);
  
  const filtered = searchTerm
    ? jurPersonen.filter(j => j.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    : jurPersonen;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative mt-1">
      <div 
        className={`flex items-center border rounded-md bg-white cursor-pointer text-sm h-9 px-3 ${
          kiEmpfehlung?.ki_empfehlung?.absender && value ? 'ring-2 ring-green-400' : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="absender-search-trigger"
      >
        <Search className="w-3.5 h-3.5 text-slate-400 mr-2 shrink-0" />
        {isOpen ? (
          <input
            autoFocus
            className="flex-1 outline-none bg-transparent text-sm"
            placeholder="Absender suchen..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onClick={e => e.stopPropagation()}
            data-testid="absender-search-input"
          />
        ) : (
          <span className={`flex-1 truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>
            {selected?.name || 'Absender auswählen...'}
          </span>
        )}
        {value && (
          <button 
            className="ml-1 text-slate-400 hover:text-slate-600" 
            onClick={e => { e.stopPropagation(); onChange(''); setSearchTerm(''); }}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto">
          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 border-b"
            onClick={() => { onChange(''); setSearchTerm(''); setIsOpen(false); }}
          >
            -- Nicht angegeben --
          </button>
          {filtered.length === 0 && searchTerm && (
            <div className="px-3 py-2 text-xs text-slate-400">Keine Ergebnisse für "{searchTerm}"</div>
          )}
          {filtered.map(j => (
            <button
              key={j.id}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-50 last:border-0 ${
                j.id === value ? 'bg-blue-50 font-medium' : ''
              }`}
              onClick={() => { onChange(j.id); setSearchTerm(''); setIsOpen(false); }}
            >
              <span>{j.name}</span>
              {j.ort && <span className="text-xs text-slate-400 ml-2">{j.ort}</span>}
              {j.typ && <span className="text-[10px] text-slate-400 ml-1">({j.typ})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function PosteingangZuordnungsForm({
  // Form-State
  form, setForm,
  // Selected document
  selectedDoc,
  // Daten
  klienten, jurPersonen, massnahmen, aufgaben, kiEmpfehlung,
  // Klient-Suche
  showKlientSearch, setShowKlientSearch, klientSearchQuery, setKlientSearchQuery,
  // Dialog-Öffner
  openNeuePersonDialog, openNeueInstitutionDialog,
  openNeueMassnahmeDialog, openNeueAufgabeDialog,
  // Nachanalyse
  showNachanalyse, setShowNachanalyse,
  nachanalyseEckdaten, setNachanalyseEckdaten,
  nachanalyseLoading, nachanalyseErgebnis, handleNachanalyse,
  // Formular-Analyse
  showFormularAnalyse, setShowFormularAnalyse,
  formularAnalyseLoading, formularAnalyseErgebnis, handleFormularAnalyse,
  // Wiedervorlage
  wiedervorlageLoading, wiedervorlageErstellt, handleBescheidWiedervorlage,
  // KI-Panel Props
  setKiEmpfehlung, loadingKiEmpfehlung, analyseResult, applyKiEmpfehlung,
  showDocChat, setShowDocChat, docChatInput, setDocChatInput,
  docChatMessages, docChatLoading, sendDocChatMessage,
  athenaLernt, neuesMuster, setNeuesMuster, athenaStats,
  // Speichern + Navigation
  handleSave, onVerlassen,
}) {
  // Aufgaben nach Klient filtern (lokal berechnet)
  const filteredAufgaben = form.klient_id
    ? aufgaben.filter(a => a.klient_id === form.klient_id)
    : aufgaben;

  return (
    <>
      {/* Scrollbarer Bereich */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* KI-Panel (Empfehlung + Analyse + Athena-Chat + Lernmodus) */}
        <PosteingangKiPanel
          kiEmpfehlung={kiEmpfehlung}
          setKiEmpfehlung={setKiEmpfehlung}
          loadingKiEmpfehlung={loadingKiEmpfehlung}
          analyseResult={analyseResult}
          applyKiEmpfehlung={applyKiEmpfehlung}
          openNeuePersonDialog={openNeuePersonDialog}
          showDocChat={showDocChat}
          setShowDocChat={setShowDocChat}
          docChatInput={docChatInput}
          setDocChatInput={setDocChatInput}
          docChatMessages={docChatMessages}
          docChatLoading={docChatLoading}
          sendDocChatMessage={sendDocChatMessage}
          athenaLernt={athenaLernt}
          neuesMuster={neuesMuster}
          setNeuesMuster={setNeuesMuster}
          athenaStats={athenaStats}
        />

                {/* 1. VON (Absender) */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-600 font-medium">Von (Absender)</Label>
                    <div className="flex items-center gap-1">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => openNeueInstitutionDialog(kiEmpfehlung?.ki_empfehlung?.absender_erkannt || '')}
                        title="Neue Institution anlegen"
                        data-testid="add-institution-btn"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <AbsenderSuche
                    value={form.von_institution_id}
                    onChange={v => setForm(p => ({ ...p, von_institution_id: v }))}
                    jurPersonen={jurPersonen}
                    kiEmpfehlung={kiEmpfehlung}
                  />
                  {kiEmpfehlung?.ki_empfehlung?.absender_erkannt && !form.von_institution_id && (
                    <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                      <p className="text-amber-700 flex items-center gap-1 mb-1">
                        <Sparkles className="w-3 h-3" />
                        KI erkannt: <span className="font-medium">"{kiEmpfehlung.ki_empfehlung.absender_erkannt}"</span>
                      </p>
                      <div className="flex gap-2 mt-1">
                        {(() => {
                          // Prüfe ob eine Institution mit ähnlichem Namen existiert
                          const absenderLower = kiEmpfehlung.ki_empfehlung.absender_erkannt.toLowerCase();
                          const matchingInstitution = jurPersonen.find(j => 
                            j.name.toLowerCase().includes(absenderLower) || 
                            absenderLower.includes(j.name.toLowerCase())
                          );
                          
                          if (matchingInstitution) {
                            return (
                              <button 
                                className="text-green-600 hover:text-green-700 underline font-medium"
                                onClick={() => setForm(p => ({ ...p, von_institution_id: matchingInstitution.id }))}
                              >
                                ✓ "{matchingInstitution.name}" verwenden
                              </button>
                            );
                          } else {
                            return (
                              <button 
                                className="text-blue-600 hover:text-blue-700 underline font-medium"
                                onClick={() => openNeueInstitutionDialog(kiEmpfehlung.ki_empfehlung.absender_erkannt)}
                              >
                                + Als neue Institution anlegen
                              </button>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {/* 2. FÜR (Betreuter) */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-600 font-medium">Für (Betreuter)</Label>
                    <div className="flex gap-1">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                        onClick={() => setShowKlientSearch(!showKlientSearch)}
                        title="Suchen"
                      >
                        <Search className="w-3 h-3" />
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => openNeuePersonDialog(kiEmpfehlung?.ki_empfehlung?.person_zum_anlegen || kiEmpfehlung?.ki_empfehlung?.betrifft_person || '')}
                        title="Neue Person anlegen"
                        data-testid="add-person-btn"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Auto-Zuordnungs-Hinweis */}
                  {selectedDoc?.auto_zuordnung?.methode && selectedDoc?.klient_name && (
                    <div className={`mt-1 mb-1 px-2 py-1 rounded text-xs flex items-center gap-1.5 ${
                      selectedDoc.auto_zuordnung.konfidenz === 'hoch' ? 'bg-green-50 text-green-700 border border-green-200' :
                      selectedDoc.auto_zuordnung.konfidenz === 'mittel' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                      'bg-slate-50 text-slate-600 border border-slate-200'
                    }`} data-testid="auto-zuordnung-hint">
                      <Zap className="w-3 h-3" />
                      Auto-Zuordnung ({selectedDoc.auto_zuordnung.methode === 'aktenzeichen' ? 'Aktenzeichen' : 
                        selectedDoc.auto_zuordnung.methode === 'absender_netzwerk' ? 'Absender im Netzwerk' :
                        selectedDoc.auto_zuordnung.methode === 'absender_person' ? 'Absender bekannt' :
                        selectedDoc.auto_zuordnung.methode === 'name_matching' ? 'Name erkannt' :
                        selectedDoc.auto_zuordnung.methode === 'claude_analyse' ? 'KI-Analyse' :
                        selectedDoc.auto_zuordnung.methode}): <strong>{selectedDoc.klient_name}</strong>
                    </div>
                  )}
                  
                  {/* Suchfeld */}
                  {showKlientSearch && (
                    <div className="mt-1 mb-2">
                      <Input
                        type="text"
                        placeholder="Betreuten suchen..."
                        value={klientSearchQuery}
                        onChange={e => setKlientSearchQuery(e.target.value)}
                        className="text-sm"
                        autoFocus
                      />
                    </div>
                  )}
                  
                  <Select 
                    value={form.klient_id || 'none'} 
                    onValueChange={v => {
                      setForm(p => ({ ...p, klient_id: v === 'none' ? '' : v, massnahme_id: '', aufgabe_id: '' }));
                    }}
                  >
                    <SelectTrigger className={`mt-1 ${
                      kiEmpfehlung?.ki_empfehlung?.gefundener_klient && form.klient_id === kiEmpfehlung.ki_empfehlung.gefundener_klient.id 
                        ? 'ring-2 ring-green-400' 
                        : form.klient_id === 'bueroorganisation' 
                        ? 'ring-2 ring-blue-400' 
                        : ''
                    }`}>
                      <SelectValue placeholder="Betreuten auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Nicht zugeordnet --</SelectItem>
                      <SelectItem value="bueroorganisation" className="font-medium text-blue-600">📁 Büroorganisation</SelectItem>
                      
                      <div className="px-2 py-1 text-xs text-slate-400 mt-1">Betreute</div>
                      {(klientSearchQuery 
                        ? klienten.filter(k => 
                            `${k.vorname} ${k.nachname} ${k.fallnummer || ''}`.toLowerCase().includes(klientSearchQuery.toLowerCase())
                          )
                        : klienten
                      ).map(k => (
                        <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname} {k.fallnummer && `(${k.fallnummer})`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {kiEmpfehlung?.ki_empfehlung?.betrifft_person && !form.klient_id && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-xs text-amber-700 flex items-center gap-1 font-medium">
                        <Sparkles className="w-3 h-3" />
                        KI erkannte Person: "{kiEmpfehlung.ki_empfehlung.betrifft_person}"
                      </p>
                      
                      {/* Ähnliche Personen anzeigen */}
                      {(() => {
                        const erkannterName = (kiEmpfehlung.ki_empfehlung.betrifft_person || '').toLowerCase();
                        const aehnliche = klienten.filter(k => {
                          const vollname = `${k.vorname || ''} ${k.nachname || ''}`.toLowerCase();
                          const nachname = (k.nachname || '').toLowerCase();
                          return vollname.includes(erkannterName) || 
                                 erkannterName.includes(nachname) ||
                                 nachname.includes(erkannterName.split(' ')[0]);
                        }).slice(0, 3);
                        
                        if (aehnliche.length > 0) {
                          return (
                            <div className="mt-2">
                              <p className="text-xs text-slate-600 mb-1">Ähnliche Betreute gefunden - mit einem Klick übernehmen:</p>
                              <div className="flex flex-wrap gap-1">
                                {aehnliche.map(k => (
                                  <Button
                                    key={k.id}
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs bg-white hover:bg-green-50 hover:border-green-400"
                                    onClick={() => {
                                      setForm(p => ({ ...p, klient_id: k.id }));
                                      toast.success(`${k.vorname} ${k.nachname} übernommen`);
                                    }}
                                  >
                                    <Check className="w-3 h-3 mr-1 text-green-600" />
                                    {k.vorname} {k.nachname}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      {kiEmpfehlung.ki_empfehlung.person_zum_anlegen && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="mt-2 h-7 text-xs text-green-700 hover:bg-green-50 hover:border-green-400"
                          onClick={() => openNeuePersonDialog(kiEmpfehlung.ki_empfehlung.person_zum_anlegen)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Neue Person anlegen: {kiEmpfehlung.ki_empfehlung.person_zum_anlegen}
                        </Button>
                      )}
                      
                      {/* Nachanalyse mit Eckdaten */}
                      <div className="mt-3 pt-3 border-t border-amber-200">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => setShowNachanalyse(!showNachanalyse)}
                        >
                          <Search className="w-3 h-3 mr-1" />
                          {showNachanalyse ? 'Nachanalyse ausblenden' : 'Mit Hinweis erneut analysieren'}
                        </Button>
                        
                        {showNachanalyse && (
                          <div className="mt-2 space-y-2">
                            <p className="text-xs text-slate-600">
                              Geben Sie einen Namen oder Hinweis ein, und Zeo durchsucht das Dokument gründlicher:
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="text"
                                placeholder="Nachname..."
                                value={nachanalyseEckdaten.nachname}
                                onChange={e => setNachanalyseEckdaten(p => ({ ...p, nachname: e.target.value }))}
                                className="h-8 text-xs"
                              />
                              <Input
                                type="text"
                                placeholder="Vorname (optional)..."
                                value={nachanalyseEckdaten.vorname}
                                onChange={e => setNachanalyseEckdaten(p => ({ ...p, vorname: e.target.value }))}
                                className="h-8 text-xs"
                              />
                            </div>
                            <Input
                              type="text"
                              placeholder="Zusätzlicher Hinweis (z.B. 'Person im Brief erwähnt')..."
                              value={nachanalyseEckdaten.hinweis}
                              onChange={e => setNachanalyseEckdaten(p => ({ ...p, hinweis: e.target.value }))}
                              className="h-8 text-xs"
                            />
                            <Button
                              type="button"
                              size="sm"
                              className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={handleNachanalyse}
                              disabled={nachanalyseLoading}
                            >
                              {nachanalyseLoading ? (
                                <>
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  Analysiere...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  Dokument erneut analysieren
                                </>
                              )}
                            </Button>
                            
                            {/* Nachanalyse-Ergebnis */}
                            {nachanalyseErgebnis && nachanalyseErgebnis.person_gefunden && (
                              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-xs font-medium text-green-700 flex items-center gap-1">
                                  <Check className="w-3 h-3" />
                                  Gefundene Daten:
                                </p>
                                <div className="mt-1 text-xs text-green-800 space-y-1">
                                  {nachanalyseErgebnis.gefundene_person.vorname && (
                                    <p><strong>Name:</strong> {nachanalyseErgebnis.gefundene_person.vorname} {nachanalyseErgebnis.gefundene_person.nachname}</p>
                                  )}
                                  {nachanalyseErgebnis.gefundene_person.geburtsdatum && (
                                    <p><strong>Geb.:</strong> {nachanalyseErgebnis.gefundene_person.geburtsdatum}</p>
                                  )}
                                  {nachanalyseErgebnis.gefundene_person.adresse?.strasse && (
                                    <p><strong>Adresse:</strong> {nachanalyseErgebnis.gefundene_person.adresse.strasse} {nachanalyseErgebnis.gefundene_person.adresse.hausnummer}, {nachanalyseErgebnis.gefundene_person.adresse.plz} {nachanalyseErgebnis.gefundene_person.adresse.ort}</p>
                                  )}
                                  {nachanalyseErgebnis.gefundene_person.aktenzeichen && (
                                    <p><strong>Aktenzeichen:</strong> {nachanalyseErgebnis.gefundene_person.aktenzeichen}</p>
                                  )}
                                  {nachanalyseErgebnis.dokument_kategorie && (
                                    <p><strong>Kategorie:</strong> {nachanalyseErgebnis.dokument_kategorie}</p>
                                  )}
                                </div>
                                
                                {/* Daten übernehmen Button */}
                                {nachanalyseErgebnis.gefundene_person.nachname && !nachanalyseErgebnis.matched_klient && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="mt-2 h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => {
                                      const p = nachanalyseErgebnis.gefundene_person;
                                      openNeuePersonDialog(`${p.vorname || ''} ${p.nachname || ''}`.trim());
                                    }}
                                  >
                                    <UserPlus className="w-3 h-3 mr-1" />
                                    Als neue Person anlegen
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* 3. BEZÜGLICH (Maßnahme) - nur wenn Klient ausgewählt */}
                {form.klient_id && form.klient_id !== 'bueroorganisation' && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-600 font-medium">Bezüglich (Maßnahme)</Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={openNeueMassnahmeDialog}
                        title="Neue Maßnahme anlegen"
                        data-testid="add-massnahme-btn"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <Select value={form.massnahme_id || 'none'} onValueChange={v => setForm(p => ({ ...p, massnahme_id: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Maßnahme auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Keine Maßnahme --</SelectItem>
                        {massnahmen.length === 0 ? (
                          <div className="px-2 py-2 text-xs text-slate-400">Keine Maßnahmen vorhanden</div>
                        ) : (
                          massnahmen.map(m => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.typ || m.bezeichnung || 'Maßnahme'} {m.institution_name && `(${m.institution_name})`}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* 4. AUFGABE - nur wenn Klient ausgewählt */}
                {form.klient_id && form.klient_id !== 'bueroorganisation' && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-600 font-medium">Aufgabe zuordnen</Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={openNeueAufgabeDialog}
                        title="Neue Aufgabe anlegen"
                        data-testid="add-aufgabe-btn"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <Select value={form.aufgabe_id || 'none'} onValueChange={v => setForm(p => ({ ...p, aufgabe_id: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Aufgabe auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Keine Aufgabe --</SelectItem>
                        {filteredAufgaben.length === 0 ? (
                          <div className="px-2 py-2 text-xs text-slate-400">Keine offenen Aufgaben</div>
                        ) : (
                          filteredAufgaben.map(a => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.titel} {a.faellig_am && `(${new Date(a.faellig_am).toLocaleDateString('de-DE')})`}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* ====== DOKUMENTEN-KATEGORISIERUNG ====== */}
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <Label className="text-xs text-slate-600 font-medium flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    Dokumenten-Kategorie
                  </Label>
                  <Select 
                    value={form.dokumentkategorie || 'none'} 
                    onValueChange={v => setForm(p => ({ ...p, dokumentkategorie: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Kategorie auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Nicht kategorisiert --</SelectItem>
                      <SelectItem value="schriftverkehr">
                        <span className="flex items-center gap-2">📨 Schriftverkehr (Korrespondenz, Briefe, E-Mails)</span>
                      </SelectItem>
                      <SelectItem value="belege">
                        <span className="flex items-center gap-2">🧾 Belege (Rechnungen, Quittungen, Kontoauszüge)</span>
                      </SelectItem>
                      <SelectItem value="bescheide">
                        <span className="flex items-center gap-2">📋 Bescheide (Rente, Pflege, Bewilligungen)</span>
                      </SelectItem>
                      <SelectItem value="antraege_formulare">
                        <span className="flex items-center gap-2">📝 Anträge & Formulare (zum Ausfüllen)</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Zusatzfelder für Bescheide */}
                  {form.dokumentkategorie === 'bescheide' && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Bescheid-Typ</Label>
                          <Select 
                            value={form.bescheid_typ || 'none'} 
                            onValueChange={v => setForm(p => ({ ...p, bescheid_typ: v === 'none' ? '' : v }))}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Typ..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- Typ --</SelectItem>
                              <SelectItem value="rente">Rentenbescheid</SelectItem>
                              <SelectItem value="pflege">Pflegegrad</SelectItem>
                              <SelectItem value="grundsicherung">Grundsicherung</SelectItem>
                              <SelectItem value="wohngeld">Wohngeld</SelectItem>
                              <SelectItem value="krankenkasse">Krankenkasse</SelectItem>
                              <SelectItem value="gericht">Gerichtsbeschluss</SelectItem>
                              <SelectItem value="sonstiges">Sonstiges</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Gültig bis</Label>
                          <Input
                            type="date"
                            className="h-8 text-xs"
                            value={form.bescheid_gueltig_bis || ''}
                            onChange={e => setForm(p => ({ ...p, bescheid_gueltig_bis: e.target.value }))}
                          />
                        </div>
                      </div>
                      
                      {/* Wiedervorlage erstellen Button */}
                      {form.bescheid_gueltig_bis && form.klient_id && form.klient_id !== 'bueroorganisation' && (
                        <Button
                          type="button"
                          size="sm"
                          className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white mt-2"
                          onClick={handleBescheidWiedervorlage}
                          disabled={wiedervorlageLoading || wiedervorlageErstellt}
                        >
                          {wiedervorlageLoading ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              Erstelle Wiedervorlage...
                            </>
                          ) : wiedervorlageErstellt ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Wiedervorlage erstellt
                            </>
                          ) : (
                            <>
                              <Calendar className="w-3 h-3 mr-1" />
                              Wiedervorlage erstellen (4 Wochen vor Ablauf)
                            </>
                          )}
                        </Button>
                      )}
                      
                      {/* Wiedervorlage-Ergebnis */}
                      {wiedervorlageErstellt && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md text-xs">
                          <p className="font-medium text-green-700 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            Wiedervorlage erstellt!
                          </p>
                          <p className="text-green-600 mt-1">
                            Fällig am: <strong>{new Date(wiedervorlageErstellt.wiedervorlage_datum).toLocaleDateString('de-DE')}</strong>
                          </p>
                          <p className="text-green-600">
                            Bescheid läuft ab: {new Date(wiedervorlageErstellt.bescheid_ablauf).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      )}
                      
                      {!form.bescheid_gueltig_bis && (
                        <p className="text-xs text-blue-600 mt-2">
                          💡 Geben Sie ein Gültigkeitsdatum ein, um automatisch eine Wiedervorlage zu erstellen.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Zusatzfelder für Anträge/Formulare */}
                  {form.dokumentkategorie === 'antraege_formulare' && (
                    <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-md space-y-2">
                      <div>
                        <Label className="text-xs">Formular-Typ</Label>
                        <Select 
                          value={form.formular_typ || 'none'} 
                          onValueChange={v => setForm(p => ({ ...p, formular_typ: v === 'none' ? '' : v }))}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Formular-Typ..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-- Typ --</SelectItem>
                            <SelectItem value="wohngeld">Wohngeldantrag</SelectItem>
                            <SelectItem value="grundsicherung">Grundsicherungsantrag</SelectItem>
                            <SelectItem value="pflege">Pflegeantrag</SelectItem>
                            <SelectItem value="rente">Rentenantrag</SelectItem>
                            <SelectItem value="vollmacht">Vollmacht</SelectItem>
                            <SelectItem value="schweigepflichtentbindung">Schweigepflichtentbindung</SelectItem>
                            <SelectItem value="sonstiges">Sonstiges Formular</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-purple-600">
                        Zeo kann helfen, komplexe Formulare vorzubereiten und mit bekannten Daten auszufüllen.
                      </p>
                      
                      {/* Formular analysieren Button */}
                      <Button
                        type="button"
                        size="sm"
                        className="mt-2 w-full h-8 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={handleFormularAnalyse}
                        disabled={formularAnalyseLoading}
                      >
                        {formularAnalyseLoading ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Analysiere Formular...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3 h-3 mr-1" />
                            Formular analysieren & ausfüllen
                          </>
                        )}
                      </Button>
                      
                      {/* Formular-Analyse-Ergebnis */}
                      {formularAnalyseErgebnis && showFormularAnalyse && (
                        <div className="mt-3 p-3 bg-white border border-purple-300 rounded-md">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-medium text-sm text-purple-700">
                              📝 {formularAnalyseErgebnis.formular_titel || formularAnalyseErgebnis.formular_typ}
                            </h5>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => setShowFormularAnalyse(false)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
                            <div className="bg-green-100 p-2 rounded text-center">
                              <span className="font-bold text-green-700">{formularAnalyseErgebnis.felder_ausgefuellt}</span>
                              <p className="text-green-600">Ausgefüllt</p>
                            </div>
                            <div className="bg-amber-100 p-2 rounded text-center">
                              <span className="font-bold text-amber-700">{formularAnalyseErgebnis.felder_offen}</span>
                              <p className="text-amber-600">Offen</p>
                            </div>
                            <div className="bg-red-100 p-2 rounded text-center">
                              <span className="font-bold text-red-700">{formularAnalyseErgebnis.felder_unklar}</span>
                              <p className="text-red-600">Unklar</p>
                            </div>
                          </div>
                          
                          {formularAnalyseErgebnis.naechste_schritte?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-slate-600 mb-1">Nächste Schritte:</p>
                              <ul className="text-xs text-slate-500 space-y-1">
                                {formularAnalyseErgebnis.naechste_schritte.map((schritt, i) => (
                                  <li key={i} className="flex items-start gap-1">
                                    <span className="text-purple-500">→</span> {schritt}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {formularAnalyseErgebnis.besondere_hinweise?.length > 0 && (
                            <div className="mt-2 p-2 bg-amber-50 rounded text-xs">
                              <p className="font-medium text-amber-700 mb-1">⚠️ Hinweise:</p>
                              {formularAnalyseErgebnis.besondere_hinweise.map((hinweis, i) => (
                                <p key={i} className="text-amber-600">{hinweis}</p>
                              ))}
                            </div>
                          )}
                          
                          {formularAnalyseErgebnis.klient_daten_verwendet && (
                            <p className="mt-2 text-xs text-green-600">
                              ✓ Daten des Betreuten wurden verwendet
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* KI-erkannte Kategorie anzeigen */}
                  {kiEmpfehlung?.dokument_kategorie && !form.dokumentkategorie && (
                    <div className="mt-2 flex items-center gap-2">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span className="text-xs text-amber-600">
                        KI-Vorschlag: <strong>{
                          kiEmpfehlung.dokument_kategorie === 'schriftverkehr' ? 'Schriftverkehr' :
                          kiEmpfehlung.dokument_kategorie === 'belege' ? 'Belege' :
                          kiEmpfehlung.dokument_kategorie === 'bescheide' ? 'Bescheide' :
                          kiEmpfehlung.dokument_kategorie === 'antraege_formulare' ? 'Anträge/Formulare' :
                          kiEmpfehlung.dokument_kategorie
                        }</strong>
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                        onClick={() => setForm(p => ({ ...p, dokumentkategorie: kiEmpfehlung.dokument_kategorie }))}
                      >
                        Übernehmen
                      </Button>
                    </div>
                  )}
                </div>

                {/* Priorität & Tags */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Dokument priorisieren</Label>
                    <Select value={form.prioritaet || 'none'} onValueChange={v => setForm(p => ({ ...p, prioritaet: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Priorität..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Normal</SelectItem>
                        <SelectItem value="niedrig">Niedrig</SelectItem>
                        <SelectItem value="mittel">Mittel</SelectItem>
                        <SelectItem value="hoch">Hoch</SelectItem>
                        <SelectItem value="dringend">Dringend</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Dokument taggen mit</Label>
                    <Input 
                      value={form.tags} 
                      onChange={e => setForm(p => ({ ...p, tags: e.target.value }))}
                      placeholder="z.B. Bescheid, Mahnung..."
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Checkboxen */}
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                    <input type="checkbox" checked={form.spaeter_bearbeiten} onChange={e => setForm(p => ({ ...p, spaeter_bearbeiten: e.target.checked }))} className="rounded" />
                    <Clock className="w-4 h-4 text-slate-400" />
                    später bearbeiten
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                    <input type="checkbox" checked={form.anderer_bearbeiten} onChange={e => setForm(p => ({ ...p, anderer_bearbeiten: e.target.checked }))} className="rounded" />
                    <User className="w-4 h-4 text-slate-400" />
                    anderer soll bearbeiten
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                    <input type="checkbox" checked={form.person_informieren} onChange={e => setForm(p => ({ ...p, person_informieren: e.target.checked }))} className="rounded" />
                    <MessageSquare className="w-4 h-4 text-slate-400" />
                    andere Person informieren
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                    <input type="checkbox" checked={form.termin_anlegen} onChange={e => setForm(p => ({ ...p, termin_anlegen: e.target.checked }))} className="rounded" />
                    <Calendar className="w-4 h-4 text-slate-400" />
                    Termin/Aufgabe anlegen
                  </label>
                </div>

                {/* Notiz */}
                <div>
                  <Label className="text-xs text-slate-600">Notiz</Label>
                  <Textarea 
                    value={form.notiz}
                    onChange={e => setForm(p => ({ ...p, notiz: e.target.value }))}
                    rows={2}
                    placeholder="Optionale Notiz..."
                    className="mt-1"
                  />
                </div>

      </div>

      {/* Fixierter Footer: Speichern-Buttons */}
      <div className="p-3 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onVerlassen} data-testid="verlassen-btn">
          <X className="w-4 h-4 mr-1" /> Verlassen
        </Button>
        <Button variant="outline" size="sm" onClick={() => handleSave(true)} data-testid="speichern-naechstes-btn">
          <SkipForward className="w-4 h-4 mr-1" /> Speichern & Nächstes
        </Button>
        <Button size="sm" onClick={() => handleSave(false)} data-testid="speichern-btn">
          <Save className="w-4 h-4 mr-1" /> Speichern
        </Button>
      </div>
    </>
  );
}
